import { useCallback, useEffect, useId, useMemo, useState } from 'react'
import ApiService from '@services/api'
import {
  approvePortalAccessRequest,
  changePortalAccessRequestClient,
  generatePortalTemporaryPasswordForRequest,
  getPortalAccessRequests,
  rejectPortalAccessRequest,
  removePortalAccessRequestUser,
  resendPortalAccessLink,
} from '@services/portalService'
import { useNotification } from '@context/NotificationContext'
import { useAuth } from '@context/AuthContext'
import Modal from '@components/shared/Modal'
import PortalTemporaryPasswordModal from './PortalTemporaryPasswordModal'
import './accessRequests.css'

const STATUS_META = {
  PENDING: { label: 'Pendente', className: 'pending' },
  APPROVED: { label: 'Aprovada', className: 'approved' },
  REJECTED: { label: 'Recusada', className: 'rejected' },
}

function formatDateTime(value) {
  if (!value) return '—'
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value))
}

function normalizeSearch(value) {
  return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('pt-BR')
}

function PortalClientAutocomplete({ clients, disabled, value, onChange }) {
  const listboxId = useId()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)

  useEffect(() => {
    const selectedClient = clients.find((client) => String(client.id) === String(value))
    if (selectedClient) setQuery(selectedClient.nome)
    else if (!value) setQuery('')
  }, [clients, value])

  const suggestions = useMemo(() => {
    const normalizedQuery = normalizeSearch(query.trim())
    const matchingClients = normalizedQuery
      ? clients.filter((client) => normalizeSearch(client.nome).includes(normalizedQuery))
      : clients
    return matchingClients.slice(0, 10)
  }, [clients, query])

  const selectClient = (client) => {
    setQuery(client.nome)
    onChange(String(client.id))
    setOpen(false)
    setActiveIndex(-1)
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setOpen(false)
      setActiveIndex(-1)
      return
    }

    if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && suggestions.length) {
      event.preventDefault()
      setOpen(true)
      const direction = event.key === 'ArrowDown' ? 1 : -1
      setActiveIndex((current) => {
        const next = current + direction
        if (next < 0) return suggestions.length - 1
        if (next >= suggestions.length) return 0
        return next
      })
      return
    }

    if (event.key === 'Enter' && open && activeIndex >= 0 && suggestions[activeIndex]) {
      event.preventDefault()
      selectClient(suggestions[activeIndex])
    }
  }

  return (
    <div className="portal-client-combobox">
      <i className="fas fa-magnifying-glass" aria-hidden="true" />
      <input
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-controls={listboxId}
        aria-expanded={open}
        aria-activedescendant={activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined}
        autoComplete="off"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value)
          onChange('')
          setOpen(true)
          setActiveIndex(-1)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => { setOpen(false); setActiveIndex(-1) }}
        onKeyDown={handleKeyDown}
        placeholder={disabled ? 'Carregando empresas...' : 'Digite para buscar uma empresa'}
        disabled={disabled}
      />
      {open && !disabled ? (
        <ul id={listboxId} className="portal-client-options" role="listbox">
          {suggestions.length ? suggestions.map((client, index) => (
            <li
              id={`${listboxId}-option-${index}`}
              key={client.id}
              role="option"
              aria-selected={index === activeIndex}
              className={index === activeIndex ? 'active' : ''}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => selectClient(client)}
            >
              <i className="far fa-building" aria-hidden="true" />
              <span>{client.nome}</span>
            </li>
          )) : <li className="portal-client-no-results">Nenhuma empresa encontrada.</li>}
        </ul>
      ) : null}
    </div>
  )
}

function AccessRequestActionModal({ action, processing, onClose, onConfirm }) {
  if (!action) return null

  const metadata = {
    reject: {
      title: 'Reprovar solicitação',
      icon: 'fa-user-xmark',
      confirmText: 'Reprovar solicitação',
      message: `Deseja reprovar a solicitação de acesso de ${action.request.nome}? O solicitante não receberá acesso ao portal.`,
      destructive: true,
    },
    temporaryPassword: {
      title: 'Gerar nova senha temporária',
      icon: 'fa-key',
      confirmText: 'Gerar senha',
      message: `A senha atual de ${action.request.nome} deixará de funcionar e todas as sessões ativas serão encerradas.`,
      destructive: true,
    },
    resendLink: {
      title: 'Reenviar link de acesso',
      icon: 'fa-paper-plane',
      confirmText: 'Reenviar link',
      message: `O link de acesso será enviado para ${action.request.email}, sem alterar a senha atual.`,
      destructive: false,
    },
    removeUser: {
      title: 'Remover usuário do portal',
      icon: 'fa-user-slash',
      confirmText: 'Remover usuário',
      message: `Remover ${action.request.nome} do Adjutorem IA? O usuário, o vínculo com a empresa e as sessões serão excluídos permanentemente.`,
      destructive: true,
    },
  }[action.type]

  return (
    <Modal
      isOpen
      onClose={processing ? () => {} : onClose}
      title={metadata.title}
      icon={metadata.icon}
      subtitle={action.request.email}
      size="small"
      closeOnOverlayClick={!processing}
      footer={
        <>
          <button type="button" className="btn btn-outline" onClick={onClose} disabled={processing}>Cancelar</button>
          <button type="button" className={metadata.destructive ? 'btn btn-danger' : 'btn btn-primary'} onClick={onConfirm} disabled={processing}>
            <i className={`fas ${processing ? 'fa-spinner fa-spin' : metadata.icon}`} /> {processing ? 'Processando...' : metadata.confirmText}
          </button>
        </>
      }
    >
      <div className="portal-access-modal-message">
        <i className={`fas ${metadata.icon}`} aria-hidden="true" />
        <p>{metadata.message}</p>
      </div>
    </Modal>
  )
}

export default function AccessRequestsTab() {
  const { showError, showSuccess } = useNotification()
  const { canCreateUsers } = useAuth()
  const [requests, setRequests] = useState([])
  const [clients, setClients] = useState([])
  const [filters, setFilters] = useState({ nome: '', email: '', clienteId: '' })
  const [loading, setLoading] = useState(true)
  const [processingId, setProcessingId] = useState(null)
  const [decisions, setDecisions] = useState({})
  const [temporaryCredential, setTemporaryCredential] = useState(null)
  const [actionModal, setActionModal] = useState(null)
  const [reviewRequest, setReviewRequest] = useState(null)
  const [clientChangeRequest, setClientChangeRequest] = useState(null)
  const [clientChangeId, setClientChangeId] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    const [requestResult, clientResult] = await Promise.all([
      getPortalAccessRequests(),
      ApiService.getProcessReportClients(),
    ])

    if (requestResult.success) setRequests(Array.isArray(requestResult.data) ? requestResult.data : [])
    else {
      setRequests([])
      showError(requestResult.error || 'Não foi possível carregar as solicitações do portal.')
    }

    if (clientResult.success) setClients(Array.isArray(clientResult.data) ? clientResult.data : [])
    else setClients([])
    setLoading(false)
  }, [showError])

  useEffect(() => { load() }, [load])

  const updateDecision = (requestId, field, value) => {
    setDecisions((current) => ({
      ...current,
      [requestId]: { ...(current[requestId] || {}), [field]: value },
    }))
  }

  const approve = async (request) => {
    if (!canCreateUsers) {
      showError('Somente Admin e Super Admin podem criar usuários.')
      return
    }

    const clientId = decisions[request.id]?.clientId
    if (!clientId) {
      showError('Selecione a empresa do LegalManager antes de aprovar.')
      return
    }

    setProcessingId(request.id)
    const result = await approvePortalAccessRequest(request.id, clientId, decisions[request.id]?.note || '')
    setProcessingId(null)
    if (!result.success) {
      showError(result.error || 'Não foi possível aprovar a solicitação.')
      return
    }

    showSuccess(`Acesso de ${request.nome} aprovado com sucesso.`)
    setReviewRequest(null)
    await load()
  }

  const reject = async (request) => {
    setProcessingId(request.id)
    const result = await rejectPortalAccessRequest(request.id, decisions[request.id]?.note || '')
    setProcessingId(null)
    if (!result.success) {
      showError(result.error || 'Não foi possível recusar a solicitação.')
      return
    }

    showSuccess('Solicitação recusada.')
    setActionModal(null)
    await load()
  }

  const generateTemporaryPassword = async (request) => {
    setProcessingId(request.id)
    const result = await generatePortalTemporaryPasswordForRequest(request.id)
    setProcessingId(null)
    if (!result.success || !result.data?.senhaTemporaria) {
      showError(result.error || 'Não foi possível gerar a senha temporária.')
      return
    }

    setTemporaryCredential(result.data)
    showSuccess('Nova senha temporária gerada. Copie antes de sair desta tela.')
    setActionModal(null)
  }

  const resendAccessLink = async (request) => {
    setProcessingId(request.id)
    const result = await resendPortalAccessLink(request.id)
    setProcessingId(null)
    if (!result.success) {
      showError(result.error || 'Não foi possível reenviar o link de acesso.')
      return
    }

    showSuccess(`Link de acesso reenviado para ${request.email}.`)
    setActionModal(null)
  }

  const removePortalUser = async (request) => {
    setProcessingId(request.id)
    const result = await removePortalAccessRequestUser(request.id)
    setProcessingId(null)
    if (!result.success) {
      showError(result.error || 'Não foi possível remover o usuário do portal.')
      return
    }

    showSuccess(`${request.nome} foi excluído permanentemente do Adjutorem IA.`)
    setActionModal(null)
    await load()
  }

  const changeClient = async () => {
    if (!clientChangeRequest || !clientChangeId) return

    setProcessingId(clientChangeRequest.id)
    const result = await changePortalAccessRequestClient(clientChangeRequest.id, clientChangeId)
    setProcessingId(null)
    if (!result.success) {
      showError(result.error || 'Não foi possível alterar a empresa vinculada.')
      return
    }

    showSuccess('Empresa vinculada ao usuário atualizada.')
    setClientChangeRequest(null)
    setClientChangeId('')
    await load()
  }

  const confirmAction = () => {
    if (!actionModal) return
    if (actionModal.type === 'reject') return reject(actionModal.request)
    if (actionModal.type === 'temporaryPassword') return generateTemporaryPassword(actionModal.request)
    if (actionModal.type === 'resendLink') return resendAccessLink(actionModal.request)
    if (actionModal.type === 'removeUser') return removePortalUser(actionModal.request)
  }

  const counts = useMemo(() => ({
    total: requests.length,
    pending: requests.filter((request) => request.status === 'PENDING').length,
  }), [requests])

  const filteredRequests = useMemo(() => {
    const normalizedName = normalizeSearch(filters.nome.trim())
    const normalizedEmail = normalizeSearch(filters.email.trim())

    return requests.filter((request) => {
      const matchesName = !normalizedName || normalizeSearch(request.nome).includes(normalizedName)
      const matchesEmail = !normalizedEmail || normalizeSearch(request.email).includes(normalizedEmail)
      const matchesClient = !filters.clienteId || String(request.clienteIdAprovado) === filters.clienteId
      return matchesName && matchesEmail && matchesClient
    })
  }, [filters, requests])

  return (
    <div className="portal-admin-tab">
      <header className="portal-admin-header">
        <div>
          <span className="portal-admin-eyebrow">Adjutorem IA</span>
          <h2>Usuários do Portal</h2>
          <p>Consulte todos os usuários e solicitações de acesso em uma única tabela.</p>
        </div>
      </header>

      <div className="portal-access-filters" aria-label="Filtros de usuários do Portal">
        <label>
          <span>Nome</span>
          <input value={filters.nome} onChange={(event) => setFilters((current) => ({ ...current, nome: event.target.value }))} placeholder="Buscar por nome" />
        </label>
        <label>
          <span>E-mail</span>
          <input type="search" value={filters.email} onChange={(event) => setFilters((current) => ({ ...current, email: event.target.value }))} placeholder="Buscar por e-mail" />
        </label>
        <label>
          <span>Empresa vinculada</span>
          <select value={filters.clienteId} onChange={(event) => setFilters((current) => ({ ...current, clienteId: event.target.value }))}>
            <option value="">Todas as empresas</option>
            {clients.map((client) => <option key={client.id} value={client.id}>{client.nome}</option>)}
          </select>
        </label>
      </div>

      <div className="portal-admin-summary">
        <span><strong>{filteredRequests.length}</strong> usuários encontrados</span>
        <span><strong>{counts.total}</strong> registros no Portal</span>
        <span><strong>{counts.pending}</strong> pendentes de análise</span>
        <button type="button" className="btn btn-outline" onClick={load} disabled={loading}><i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-rotate'}`} /> Atualizar</button>
      </div>

      {loading ? (
        <div className="portal-admin-empty"><i className="fas fa-spinner fa-spin" /><span>Carregando usuários do Portal...</span></div>
      ) : filteredRequests.length === 0 ? (
        <div className="portal-admin-empty"><i className="far fa-circle-check" /><strong>Nenhum usuário encontrado.</strong><span>Ajuste os filtros para localizar outro registro.</span></div>
      ) : (
        <div className="portal-access-table-wrap">
          <table className="portal-access-table">
            <thead><tr><th>Usuário</th><th>E-mail</th><th>Empresa vinculada</th><th>Status</th><th>Solicitado em</th><th>Ações</th></tr></thead>
            <tbody>{filteredRequests.map((request) => {
            const meta = STATUS_META[request.status] || { label: request.status, className: '' }
            const pending = request.status === 'PENDING'
            const approved = request.status === 'APPROVED'
            return (
              <tr key={request.id}>
                <td><strong>{request.nome}</strong><small>{request.cargo || request.telefone || '—'}</small></td>
                <td><a href={`mailto:${request.email}`}>{request.email}</a></td>
                <td><strong>{request.clienteNomeAprovado || 'Não vinculada'}</strong>{pending && request.empresa ? <small>Informada: {request.empresa}</small> : null}</td>
                <td><span className={`portal-request-status ${meta.className}`}>{meta.label}</span></td>
                <td>{formatDateTime(request.createdAt)}</td>
                <td>
                  {pending ? <div className="portal-access-table-actions">{canCreateUsers ? <button type="button" className="portal-table-action primary" onClick={() => setReviewRequest(request)} disabled={processingId === request.id}><i className="fas fa-user-check" /> Analisar</button> : null}<button type="button" className="portal-table-action" onClick={() => setActionModal({ type: 'reject', request })} disabled={processingId === request.id}><i className="fas fa-user-xmark" /> Recusar</button></div> : null}
                  {approved ? <div className="portal-access-table-actions"><button type="button" className="portal-table-icon-action" title="Reenviar link de acesso" aria-label="Reenviar link de acesso" onClick={() => setActionModal({ type: 'resendLink', request })} disabled={processingId === request.id}><i className="fas fa-paper-plane" /></button><button type="button" className="portal-table-icon-action" title="Alterar empresa vinculada" aria-label="Alterar empresa vinculada" onClick={() => { setClientChangeRequest(request); setClientChangeId(String(request.clienteIdAprovado || '')) }} disabled={processingId === request.id}><i className="fas fa-building-pen" /></button><button type="button" className="portal-table-icon-action" title="Gerar nova senha temporária" aria-label="Gerar nova senha temporária" onClick={() => setActionModal({ type: 'temporaryPassword', request })} disabled={processingId === request.id}><i className="fas fa-key" /></button><button type="button" className="portal-table-icon-action danger" title="Remover usuário" aria-label="Remover usuário" onClick={() => setActionModal({ type: 'removeUser', request })} disabled={processingId === request.id}><i className="fas fa-user-slash" /></button></div> : null}
                  {!pending && !approved ? <span className="portal-table-no-action">—</span> : null}
                </td>
              </tr>
            )
          })}</tbody>
          </table>
        </div>
      )}
      <AccessRequestActionModal
        action={actionModal}
        processing={Boolean(actionModal && processingId === actionModal.request.id)}
        onClose={() => setActionModal(null)}
        onConfirm={confirmAction}
      />
      <Modal
        isOpen={canCreateUsers && Boolean(reviewRequest)}
        onClose={() => {
          if (processingId === reviewRequest?.id) return
          setReviewRequest(null)
        }}
        title="Analisar solicitação de acesso"
        subtitle={reviewRequest?.email}
        icon="fa-user-check"
        size="small"
        closeOnOverlayClick={processingId !== reviewRequest?.id}
        footer={
          <>
            <button type="button" className="btn btn-outline" onClick={() => setReviewRequest(null)} disabled={processingId === reviewRequest?.id}>Cancelar</button>
            <button type="button" className="btn btn-danger" onClick={() => { setReviewRequest(null); setActionModal({ type: 'reject', request: reviewRequest }) }} disabled={processingId === reviewRequest?.id}>Recusar</button>
            <button type="button" className="btn btn-primary" onClick={() => approve(reviewRequest)} disabled={!decisions[reviewRequest?.id]?.clientId || processingId === reviewRequest?.id}>
              <i className={`fas ${processingId === reviewRequest?.id ? 'fa-spinner fa-spin' : 'fa-check'}`} /> {processingId === reviewRequest?.id ? 'Aprovando...' : 'Aprovar acesso'}
            </button>
          </>
        }
      >
        {reviewRequest ? <div className="portal-access-modal-form">
          <p><strong>{reviewRequest.nome}</strong> informou a empresa {reviewRequest.empresa || 'não informada'}. Selecione a empresa que ele poderá consultar no Portal.</p>
          <label>
            <span>Empresa vinculada *</span>
            <PortalClientAutocomplete clients={clients} disabled={loading || processingId === reviewRequest.id} value={decisions[reviewRequest.id]?.clientId || ''} onChange={(clientId) => updateDecision(reviewRequest.id, 'clientId', clientId)} />
          </label>
          <label>
            <span>Observação interna <small>opcional</small></span>
            <textarea rows={3} value={decisions[reviewRequest.id]?.note || ''} onChange={(event) => updateDecision(reviewRequest.id, 'note', event.target.value)} maxLength={1000} />
          </label>
        </div> : null}
      </Modal>
      <Modal
        isOpen={Boolean(clientChangeRequest)}
        onClose={() => {
          if (processingId === clientChangeRequest?.id) return
          setClientChangeRequest(null)
          setClientChangeId('')
        }}
        title="Alterar empresa vinculada"
        subtitle={clientChangeRequest?.email}
        icon="fa-building-pen"
        size="small"
        closeOnOverlayClick={processingId !== clientChangeRequest?.id}
        footer={
          <>
            <button type="button" className="btn btn-outline" onClick={() => { setClientChangeRequest(null); setClientChangeId('') }} disabled={processingId === clientChangeRequest?.id}>Cancelar</button>
            <button type="button" className="btn btn-primary" onClick={changeClient} disabled={!clientChangeId || processingId === clientChangeRequest?.id}>
              <i className={`fas ${processingId === clientChangeRequest?.id ? 'fa-spinner fa-spin' : 'fa-check'}`} /> {processingId === clientChangeRequest?.id ? 'Salvando...' : 'Salvar empresa'}
            </button>
          </>
        }
      >
        <div className="portal-access-modal-form">
          <p>Escolha a única empresa do LegalManager que este usuário poderá consultar no portal.</p>
          <label>
            <span>Empresa *</span>
            <PortalClientAutocomplete clients={clients} disabled={loading || processingId === clientChangeRequest?.id} value={clientChangeId} onChange={setClientChangeId} />
          </label>
        </div>
      </Modal>
      <PortalTemporaryPasswordModal credential={temporaryCredential} onClose={() => setTemporaryCredential(null)} />
    </div>
  )
}
